import React, {
  type ChangeEventHandler,
  type FC,
  type FormEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router';

import { useApolloClient } from '@apollo/client/react';

import { useAbeetContext } from '@clutter/abeet';
import { WTEventParams } from '@clutter/wt';

import { GeocodeFragment } from '@graphql/platform';

import { useResolveCheckoutURL } from '@root/components/checkout/utilities/checkout_url';
import { track } from '@root/initializers/wt';
import { generateLeadSource } from '@root/resources/lead';

import { Action, useClientDataContext } from '@shared/client_data_context';
import { GeocodeError } from '@shared/geocode_error';
import { ServiceSelection } from '@shared/service_selector/constants';

import { formatZIP } from '@utils/formatters';
import {
  GeocodeStatus,
  geocode,
  parseGeocodeEligibility,
} from '@utils/geocode';
import { getPageName } from '@utils/get_pagename_utilities';
import { useStabilizedFunction } from '@utils/hooks';
import { useTrackFunnelEntryPointViewed } from '@utils/hooks/funnel_events/use_track_funnel_entry_point_viewed';
import { useMountedRef } from '@utils/hooks/mount';
import { enrollStorageBookingFunnelTest } from '@utils/hooks/storage_booking_funnel_test';

import { FunnelEntryPointContext } from './funnel_entry_point_context';
import {
  type FunnelEntryPointContextValue,
  type FunnelEntryPointErrorKind,
} from './funnel_entry_point_context_value';
import { FunnelEntryPointSmokeTestModal } from './funnel_entry_point_smoke_test_modal';

const eligibleForService = (
  service?: ServiceSelection,
  geocode?: GeocodeFragment,
): boolean => {
  if (!geocode || !service) return false;

  const { hasMoving, hasStorage } = parseGeocodeEligibility(geocode);

  switch (service) {
    case ServiceSelection.Moving:
      return hasMoving;
    case ServiceSelection.Storage:
      return hasStorage;
    default:
      return hasStorage;
  }
};

export const FunnelEntryPoint: FC<{
  children: ReactNode;
  service?: ServiceSelection;
  wtConfig: WTEventParams & { container: string };
  emptyZipRedirectTo?: string;
}> = ({
  children,
  service = ServiceSelection.Storage,
  wtConfig,
  emptyZipRedirectTo,
}) => {
  const {
    data: { zip },
    dispatch,
  } = useClientDataContext();
  const history = useHistory();
  const resolveCheckoutURL = useResolveCheckoutURL();
  const mountedRef = useMountedRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ReactNode | undefined>(undefined);
  const [errorKind, setErrorKind] = useState<
    FunnelEntryPointErrorKind | undefined
  >(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);

  const client = useApolloClient();
  const { execute } = useAbeetContext();

  useTrackFunnelEntryPointViewed({ service: service });

  const onSubmit = useStabilizedFunction(
    async (e: FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();
        track({
          objectType: 'input:button',
          objectName: 'cta',
          label: 'Get Pricing',
          action: 'click',
          value: zip,
          metadata: {
            formData: zip,
            serviceSelection: service,
          },
          ...wtConfig,
        });

        if (!zip || zip.trim() === '') {
          if (emptyZipRedirectTo) {
            if (service === ServiceSelection.Storage) {
              await enrollStorageBookingFunnelTest(execute);
            }
            history.push(emptyZipRedirectTo);
            return;
          }

          setErrorKind('invalid');
          setError('Please enter a valid ZIP code.');
          return;
        }

        if (service === ServiceSelection.Moving) {
          setError(
            'We’re sorry, but Clutter doesn’t offer moving services in your area.',
          );
          return;
        }

        if (loading) {
          return;
        }
        setLoading(true);

        const result = await geocode(client, zip);

        setLoading(false);

        const shouldEnterCheckout = eligibleForService(service, result.geocode);

        if (shouldEnterCheckout) {
          const checkoutURL = await resolveCheckoutURL(service, result.geocode);
          dispatch({
            type: Action.SetupCheckout,
            payload: {
              leadSource: generateLeadSource({
                domain: 'www',
                pageName: getPageName({ prefix: false }),
                entryPoint: wtConfig.container,
              }),
              zip: zip,
              serviceSelection: service,
            },
          });

          if (service === ServiceSelection.Storage) {
            await enrollStorageBookingFunnelTest(execute);
          }

          history.push(checkoutURL);
        } else {
          const geocodeErrorType =
            result.status === GeocodeStatus.Valid
              ? GeocodeStatus.OSA
              : result.status;

          setErrorKind(
            geocodeErrorType === GeocodeStatus.Invalid ? 'invalid' : 'osa',
          );
          setError(
            <GeocodeError
              type={
                // If this status is valid but we're still in this branch, it
                // means we service the ZIP, but not the for desired service and
                // should show the a service specific OSA message.
                result.status === GeocodeStatus.Valid
                  ? GeocodeStatus.OSA
                  : result.status
              }
              service={service}
            />,
          );
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
  );

  const onZipChange: ChangeEventHandler<HTMLInputElement> =
    useStabilizedFunction((event) => {
      setError(undefined);
      setErrorKind(undefined);
      const value = formatZIP(event.currentTarget.value);

      track({
        action: 'input',
        label: 'zip',
        value,
        objectType: 'input:text',
        objectName: 'zip',
        ...wtConfig,
      });

      dispatch({ type: Action.SetZip, payload: { zip: value } });
    });

  const value: FunnelEntryPointContextValue = useMemo(
    () => ({
      service,
      onSubmit,
      loading,
      onZipChange,
      errorMessage: error,
      errorKind,
    }),
    [service, onSubmit, error, errorKind, loading, onZipChange],
  );

  return (
    <FunnelEntryPointContext.Provider value={value}>
      {children}
      <FunnelEntryPointSmokeTestModal
        zip={zip}
        isOpen={showModal}
        handleModalClose={() => setShowModal(false)}
      />
    </FunnelEntryPointContext.Provider>
  );
};
